import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Activity, Linkedin, Mail, Server, Database, AlertTriangle,
  Send, MessageSquare, MessageSquarePlus, FileText, ListChecks, Flame, RefreshCw,
  TrendingUp, Clock, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { relativeTime } from "@/lib/utils";

const STATUS_DOT = {
  connected: "bg-emerald-500",
  configured: "bg-emerald-500",
  app_only: "bg-emerald-500",
  ok: "bg-emerald-500",
  missing: "bg-destructive",
  needs_reconnect: "bg-amber-accent",
  pending: "bg-amber-accent",
};

const STATUS_LABEL = {
  connected: "Connected",
  configured: "Configured",
  app_only: "Connected (app-only)",
  ok: "OK",
  missing: "Missing",
  needs_reconnect: "Reconnect",
  pending: "Pending",
};

export default function OverviewPage() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch { toast.error("Dashboard load failed"); }
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const runSequencer = async () => {
    setRunning(true);
    try {
      await api.post("/sequencer/run-now");
      toast.success("Sequencer queued — polling in 10s");
      setTimeout(load, 10000);
    } catch (e) {
      toast.error(`Sequencer failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setRunning(false);
  };

  if (!data) return (
    <DashboardLayout eyebrow="Overview" title="LogiGrowth AI">
      <div className="text-sm text-muted-foreground">Loading dashboard…</div>
    </DashboardLayout>
  );

  const { connections, outbound_24h, rates_7d, pipeline, open_replies, approval_queue, anomalies, sequencer } = data;
  const overallHealth = anomalies.some((a) => a.level === "high")
    ? { dot: "bg-destructive", text: "text-destructive", border: "border-destructive/40", bg: "bg-destructive/5", label: "Action needed" }
    : anomalies.length > 0
      ? { dot: "bg-amber-accent", text: "text-amber-accent", border: "border-amber-accent/40", bg: "bg-amber-accent/5", label: "Watch list" }
      : { dot: "bg-emerald-500", text: "text-emerald-500", border: "border-emerald-500/40", bg: "bg-emerald-500/5", label: "All green" };

  return (
    <DashboardLayout
      eyebrow="Daily check"
      title="LogiGrowth Overview"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing} data-testid="overview-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" onClick={runSequencer} disabled={running} data-testid="overview-run-sequencer">
            <Activity className="w-3.5 h-3.5 mr-1.5" /> {running ? "Running…" : "Run sequencer now"}
          </Button>
        </div>
      }
    >
      <div className={`data-card mb-6 ${overallHealth.border} ${overallHealth.bg} flex items-center gap-3`} data-testid="overview-health">
        <span className={`w-3 h-3 rounded-full ${overallHealth.dot} animate-pulse`} />
        <div className="flex-1">
          <div className={`font-display font-semibold ${overallHealth.text}`}>{overallHealth.label}</div>
          <div className="text-xs text-muted-foreground">
            {anomalies.length === 0 ? "All systems operational." : `${anomalies.length} item${anomalies.length === 1 ? "" : "s"} need your attention.`}
          </div>
        </div>
        <div className="text-[0.65rem] text-muted-foreground inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> {relativeTime(data.now)}
        </div>
      </div>

      {anomalies.length > 0 && (
        <div className="data-card mb-6" data-testid="overview-anomalies">
          <div className="label-eyebrow inline-flex items-center gap-2 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-accent" /> Anomalies ({anomalies.length})
          </div>
          <ul className="space-y-1">
            {anomalies.map((a, i) => (
              <li key={`${a.level}-${a.msg.slice(0, 20)}-${i}`} className={`text-xs ${a.level === "high" ? "text-destructive" : "text-amber-accent"}`}>
                • {a.msg}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <ConnectionCard icon={Linkedin} label="LinkedIn" status={connections.linkedin.status} detail={connections.linkedin.display_name ? `${connections.linkedin.display_name} · ${connections.linkedin.company_pages} pages` : "—"} testid="conn-linkedin" />
        <ConnectionCard icon={Mail} label="M365 Email" status={connections.m365.status} detail={connections.m365.email || "Not connected"} testid="conn-m365" />
        <ConnectionCard icon={Server} label="Unipile API" status={connections.unipile.status} detail={connections.unipile.dsn || "Not configured"} testid="conn-unipile" />
        <ConnectionCard icon={Database} label="ERP Mirror" status={connections.erp.status} detail={`${(connections.erp.mirror_counts.contacts || 0).toLocaleString("en-US")} contacts · ${(connections.erp.mirror_counts.offers || 0).toLocaleString("en-US")} offers`} testid="conn-erp" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="data-card" data-testid="overview-outbound-24h">
          <div className="label-eyebrow inline-flex items-center gap-2 mb-2">
            <Send className="w-3.5 h-3.5" /> Outbound — last 24h
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Stat icon={Linkedin} label="Invites" value={outbound_24h.invites} />
            <Stat icon={MessageSquare} label="DMs" value={outbound_24h.dms} />
            <Stat icon={MessageSquarePlus} label="Comments" value={outbound_24h.comments ?? 0} />
            <Stat icon={FileText} label="Posts" value={outbound_24h.posts} />
            <Stat icon={Mail} label="Emails" value={outbound_24h.emails} />
          </div>
        </div>

        <div className="data-card" data-testid="overview-rates-7d">
          <div className="label-eyebrow inline-flex items-center gap-2 mb-2">
            <TrendingUp className="w-3.5 h-3.5" /> Conversion — last 7d
          </div>
          <div className="space-y-2 text-xs">
            <RatRow label="Invited" value={rates_7d.invited} />
            <RatRow label="Accepted" value={rates_7d.accepted} pct={rates_7d.acceptance_rate} />
            <RatRow label="Replied" value={rates_7d.replied} pct={rates_7d.reply_rate} />
          </div>
        </div>

        <div className="data-card" data-testid="overview-pipeline">
          <div className="label-eyebrow inline-flex items-center gap-2 mb-2">
            <ListChecks className="w-3.5 h-3.5" /> Pipeline
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <PipelineRow label="New" value={pipeline.new} />
            <PipelineRow label="Approval" value={pipeline.pending_approval} highlight={pipeline.pending_approval > 0} />
            <PipelineRow label="Invited" value={pipeline.invited} />
            <PipelineRow label="DM sent" value={pipeline.dm_sent} />
            <PipelineRow label="Email sent" value={pipeline.email_sent} />
            <PipelineRow label="Replied 🔥" value={pipeline.replied} highlight={pipeline.replied > 0} />
            <PipelineRow label="Won to ERP" value={pipeline.won_to_erp} />
            <PipelineRow label="Blocked (ERP)" value={pipeline.outreach_blocked} />
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="data-card" data-testid="overview-open-replies">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow inline-flex items-center gap-2">
              <Flame className="w-3.5 h-3.5 text-destructive" /> Open replies ({open_replies.length})
            </div>
            <Link to="/app/inbox" className="text-[0.65rem] text-signal-500 hover:underline" data-testid="overview-link-inbox">Open Inbox →</Link>
          </div>
          {open_replies.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">No new replies. System is humming.</p>
          ) : (
            <ul className="space-y-2">
              {open_replies.slice(0, 5).map((p) => (
                <li key={p.id} className="text-xs border-l-2 border-destructive/40 pl-2.5">
                  <div className="font-medium truncate">{p.full_name} · <span className="text-signal-500">{p.company}</span></div>
                  {p.last_reply_excerpt && <div className="text-muted-foreground italic">"{p.last_reply_excerpt.slice(0, 120)}"</div>}
                  <div className="text-[0.6rem] text-muted-foreground">{relativeTime(p.replied_at)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="data-card" data-testid="overview-approval-queue">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow inline-flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5 text-amber-accent" /> Pending your approval ({approval_queue.length})
            </div>
            <Link to="/app/inbox?tab=approval" className="text-[0.65rem] text-signal-500 hover:underline" data-testid="overview-link-approval">Review →</Link>
          </div>
          {approval_queue.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Nothing to approve — sequencer fires automatically.</p>
          ) : (
            <ul className="space-y-2">
              {approval_queue.slice(0, 5).map((p) => (
                <li key={p.id} className="text-xs border-l-2 border-amber-accent/40 pl-2.5">
                  <div className="font-medium truncate">{p.full_name} · <span className="text-muted-foreground">{p.title}</span> @ <span className="text-signal-500">{p.company}</span></div>
                  <div className="text-[0.6rem] text-muted-foreground">Fit {p.score}/100 · {p.approval_reason}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="data-card" data-testid="overview-sequencer-history">
        <div className="label-eyebrow inline-flex items-center gap-2 mb-2">
          <Activity className="w-3.5 h-3.5" /> Sequencer — last runs
        </div>
        {sequencer.last_runs.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No runs yet — fires every 30 min. Click "Run now" to test.</p>
        ) : (
          <ul className="space-y-1.5 text-xs">
            {sequencer.last_runs.map((r) => (
              <li key={r.ran_at} className="flex items-center justify-between border-b border-border/40 pb-1.5 last:border-0">
                <span className="text-muted-foreground inline-flex items-center gap-1.5">
                  <Clock className="w-3 h-3" /> {relativeTime(r.ran_at)}
                </span>
                <span className="font-mono text-[0.65rem]">
                  {r.stats.invites_sent}🔗 {r.stats.dms_sent}💬 {r.stats.emails_sent}✉️ {r.stats.queued_for_approval}🛡️
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardLayout>
  );
}

function ConnectionCard({ icon: Icon, label, status, detail, testid }) {
  const dot = STATUS_DOT[status] || "bg-muted";
  const stLabel = STATUS_LABEL[status] || status;
  return (
    <div className="data-card !p-3" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-signal-500" />
        <span className="font-display font-semibold text-xs">{label}</span>
        <span className={`w-1.5 h-1.5 rounded-full ml-auto ${dot}`} />
      </div>
      <div className="text-[0.65rem] text-muted-foreground">{stLabel}</div>
      <div className="text-[0.65rem] text-foreground/80 truncate mt-0.5">{detail}</div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-background/40 border border-border rounded-sm p-2">
      <div className="inline-flex items-center gap-1 text-[0.6rem] tracking-widest uppercase text-muted-foreground">
        <Icon className="w-2.5 h-2.5" /> {label}
      </div>
      <div className="font-mono text-base font-semibold">{value}</div>
    </div>
  );
}

function RatRow({ label, value, pct }) {
  return (
    <div className="flex items-center justify-between border-b border-border/40 pb-1 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">
        {value}
        {pct != null && <span className="text-signal-500 ml-2">({pct}%)</span>}
      </span>
    </div>
  );
}

function PipelineRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground truncate">{label}</span>
      <span className={`font-mono ${highlight && value > 0 ? "text-destructive font-bold" : ""}`}>{value}</span>
    </div>
  );
}
